import React, {useState, useEffect, useCallback} from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';

import {useStores} from '../../../Hooks';
import {MembersRequester} from '../../Domain/Groups/MembersRequester';
import SummaryCard from './SummaryCard';
import AccessSelector from './AccessSelector';

const Step3FinalReview = ({emails, permissions, accessPayload, onAccessChange, onBack, onClose, onSendSuccess}) => {
    const {modalStore, generalStore} = useStores();
    const [isSending, setIsSending] = useState(false);

    const handleSend = useCallback(async () => {
        setIsSending(true);
        try {
            const result = await MembersRequester.inviteMembers(generalStore.workspace, {
                emails: emails.map((e) => e.address),
                roles: {
                    trainingAndFieldRole: permissions.trainingAndFieldRole,
                    contentCreationRole: permissions.contentCreationRole,
                    administrationRole: permissions.administrationRole,
                },
                groups: permissions.groups.map((g) => g.GroupId),
                rolePresetId: permissions.rolePresetId || undefined,
                accessPayload: accessPayload
                    ? {includeAll: accessPayload.includeAll, individual: accessPayload.individual, isRoleVisibilityEnabled: accessPayload.isRoleVisibilityEnabled}
                    : null,
            });
            onSendSuccess(result?.inviteCount || emails.length);
            onClose();
        } catch (error) {
            console.error('Failed to send invitations:', error);
            modalStore.showErrorMessage(generalStore.translate('saveFailed') || 'Failed to send invitations.');
        } finally {
            setIsSending(false);
        }
    }, [generalStore.workspace, permissions, accessPayload, emails, onSendSuccess, onClose, modalStore, generalStore]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && !isSending) {
                e.preventDefault();
                handleSend();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isSending, handleSend]);

    const sendButtonClass = isSending
        ? 'bg-classic-button-disabled cursor-default'
        : 'bg-classic-button-normal hover:bg-classic-button-hover cursor-pointer';

    return (
        <>
            <div className="flex flex-col gap-5">
                <SummaryCard emailCount={emails.length} permissions={permissions} />
                <AccessSelector permissions={permissions} accessPayload={accessPayload} onAccessChange={onAccessChange} />
            </div>

            <div className="flex items-center justify-between pt-4">
                <div
                    className="flex items-center gap-1 rounded-button px-3 py-2 text-sm font-bold cursor-pointer text-main-normal hover:bg-panels-normal"
                    onClick={onBack}
                >
                    <ChevronLeftIcon style={{fontSize: 18}} />
                    Back
                </div>
                <div className="flex gap-3">
                    <div
                        className="rounded-button px-4 py-2 text-sm font-bold cursor-pointer text-main-normal hover:bg-panels-normal"
                        onClick={onClose}
                    >
                        Cancel
                    </div>
                    <div
                        data-testid="invite-send-button"
                        className={`rounded-button px-4 py-2 text-sm font-bold flex items-center gap-1.5 text-classic-button-normal-text ${sendButtonClass}`}
                        onClick={!isSending ? handleSend : undefined}
                    >
                        <SendOutlinedIcon style={{fontSize: 16}} />
                        Send Invitations ({emails.length})
                    </div>
                </div>
            </div>
        </>
    );
};

export default Step3FinalReview;
